import Swal from 'sweetalert2';
import axios from 'axios';

const API = process.env.REACT_APP_API_BASE_URL;

const WEB_IMAGE_CSS = `
  .web-image-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:12px; margin-top:10px; max-height:55vh; overflow-y:auto; padding:4px; }
  @media (max-width:640px){ .web-image-grid{ grid-template-columns:repeat(2,minmax(0,1fr)); } }
  .web-image-card { border-radius:10px; overflow:hidden; cursor:pointer; border:2px solid transparent; background:#f8fafc; transition:transform .15s ease, box-shadow .15s ease, border-color .15s ease; }
  .web-image-card:hover { transform:translateY(-2px); box-shadow:0 6px 16px rgba(0,0,0,.15); }
  .web-image-card.selected { border-color:#6366F1; box-shadow:0 0 0 3px rgba(99,102,241,.35); }
  .web-image-thumb-wrap { width:100%; aspect-ratio:1/1; display:flex; align-items:center; justify-content:center; background:#fff; }
  .web-image-thumb { max-width:100%; max-height:100%; object-fit:contain; }
  .web-image-title { font-size:11px; color:#475569; padding:6px 8px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin:0; text-align:center; }
  .web-image-grid.dark .web-image-card { background:#374151; }
  .web-image-grid.dark .web-image-thumb-wrap { background:#1f2937; }
  .web-image-grid.dark .web-image-title { color:#d1d5db; }
  .web-image-source { display:block; margin:0 auto; max-width:calc(100% - 16px); font-size:10px; color:#4f46e5; padding:0 0 7px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; cursor:pointer; }
  .web-image-source:hover { text-decoration:underline; }
  .web-image-grid.dark .web-image-source { color:#818cf8; }
  .swal2-container.swal-above-modals { z-index:10050 !important; }
`;

export const ensureWebImageStyles = () => {
  if (typeof document === 'undefined') return;
  const id = 'item-edit-web-image-css';
  let style = document.getElementById(id);
  if (!style) {
    style = document.createElement('style');
    style.id = id;
    document.head.appendChild(style);
  }
  style.textContent = WEB_IMAGE_CSS;
};

export const swalAbove = (...args) => {
  const raw = typeof args[0] === 'string'
    ? { title: args[0], text: args[1], icon: args[2] }
    : { ...(args[0] || {}) };
  const originalDidOpen = raw.didOpen;
  return Swal.fire({
    ...raw,
    customClass: {
      ...(raw.customClass || {}),
      container: ['swal-above-modals', raw.customClass?.container].filter(Boolean).join(' '),
    },
    didOpen: (el) => {
      const container = Swal.getContainer();
      if (container) container.style.zIndex = '10050';
      if (originalDidOpen) originalDidOpen(el);
    },
  });
};

const sourceLabel = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch (_) { return 'Source'; }
};

const showGallery = (images, isDarkMode) => {
  let selectedIndex = null;
  const galleryHtml = `
    <div class="web-image-grid ${isDarkMode ? 'dark' : ''}">
      ${images.map((img, index) => `
        <div class="web-image-card" data-web-image-index="${index}" title="${(img.title || '').replace(/"/g, '&quot;')}">
          <div class="web-image-thumb-wrap">
            <img class="web-image-thumb" src="${API}/items/web-image-proxy?url=${encodeURIComponent(img.thumbnail || img.original)}" alt="Result ${index + 1}" loading="lazy" />
          </div>
          <p class="web-image-title">${(img.title || `Option ${index + 1}`).slice(0, 60)}</p>
          ${img.source ? `<a class="web-image-source" href="${img.source}" target="_blank" rel="noopener noreferrer">🔗 ${sourceLabel(img.source)}</a>` : ''}
        </div>
      `).join('')}
    </div>
  `;

  return swalAbove({
    title: 'Is any of these usable?',
    text: 'Click an image to select it, then confirm to use it.',
    html: galleryHtml,
    showDenyButton: true,
    showCancelButton: true,
    denyButtonText: 'Search Again',
    confirmButtonText: 'Use Selected Image',
    cancelButtonText: 'None of these',
    reverseButtons: true,
    focusConfirm: false,
    heightAuto: false,
    allowOutsideClick: () => !Swal.isLoading(),
    didOpen: () => {
      const container = Swal.getHtmlContainer();
      if (!container) return;
      const confirmBtn = Swal.getConfirmButton();
      if (confirmBtn) confirmBtn.disabled = true;
      container.querySelectorAll('.web-image-thumb').forEach((thumbEl) => {
        thumbEl.addEventListener('error', () => {
          const card = thumbEl.closest('.web-image-card');
          if (card) card.remove();
          if (!container.querySelector('.web-image-card')) {
            Swal.close();
          }
        });
      });
      container.querySelectorAll('[data-web-image-index]').forEach((card) => {
        card.addEventListener('click', (e) => {
          if (e.target.closest('a')) return;
          selectedIndex = parseInt(card.getAttribute('data-web-image-index'), 10);
          container.querySelectorAll('.web-image-card.selected').forEach((c) => c.classList.remove('selected'));
          card.classList.add('selected');
          if (confirmBtn) confirmBtn.disabled = false;
        });
      });
    },
    preConfirm: () => {
      if (selectedIndex === null) {
        Swal.showValidationMessage('Please select an image first');
        return false;
      }
      return selectedIndex;
    },
  });
};

const downloadWebImage = async (webImage) => {
  swalAbove({
    title: 'Fetching selected image...',
    allowOutsideClick: false,
    allowEscapeKey: false,
    heightAuto: false,
    didOpen: () => Swal.showLoading(),
  });
  try {
    const proxyUrl = `${API}/items/web-image-proxy?url=${encodeURIComponent(webImage.original)}`;
    const response = await fetch(proxyUrl);
    if (!response.ok) throw new Error('Failed to download the selected image');
    const blob = await response.blob();
    if (!blob.type.startsWith('image/')) throw new Error('Selected URL is not a valid image');
    if (blob.size > 5 * 1024 * 1024) throw new Error('Selected image exceeds the 5MB limit');
    const extension = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
    const file = new File([blob], `web-image-${Date.now()}.${extension}`, { type: blob.type });
    Swal.close();
    return file;
  } catch (error) {
    Swal.close();
    await swalAbove('Error', error.message || 'Failed to apply web image', 'error');
    return null;
  }
};

/**
 * Prompt → search → gallery → download. Returns a validated File or null.
 */
export const pickWebImage = async ({ defaultQuery = '', isDarkMode = false } = {}) => {
  ensureWebImageStyles();
  let query = defaultQuery;

  for (;;) {
    const prompt = await swalAbove({
      title: 'Auto-Find Image on Web',
      input: 'text',
      inputLabel: 'Search query (brand & description)',
      inputValue: query,
      inputPlaceholder: 'e.g. Havells ceiling fan',
      showCancelButton: true,
      confirmButtonText: 'Search Web',
      cancelButtonText: 'Cancel',
      heightAuto: false,
      inputValidator: (value) => (!value || !value.trim()) ? 'Please enter a search query' : null,
    });
    if (!prompt.value) return null;
    query = prompt.value.trim();

    let images = [];
    try {
      swalAbove({
        title: 'Searching the web...',
        html: `Looking for images of "<strong>${query.replace(/</g, '&lt;')}</strong>"`,
        allowOutsideClick: false,
        allowEscapeKey: false,
        heightAuto: false,
        didOpen: () => Swal.showLoading(),
      });
      const response = await axios.get(`${API}/items/search-web-images`, {
        params: { q: query, limit: 18 },
      });
      images = response.data?.images || [];
      Swal.close();
      if (!images.length) {
        await swalAbove('No Results', 'No images found for that query.', 'info');
        continue;
      }
    } catch (error) {
      Swal.close();
      await swalAbove('Error', error.response?.data?.error || error.message || 'Failed to search images on the web', 'error');
      continue;
    }

    const result = await showGallery(images, isDarkMode);
    if (result.isConfirmed && result.value !== null && result.value !== undefined) {
      return downloadWebImage(images[result.value]);
    }
    if (result.isDenied) continue;
    return null;
  }
};
